import { getParentTargetOrigin } from './parentOrigin';

export type MessageEventType =
    | 'attachmentStateChanged'
    | 'hashchange'
    | 'aiassitantcontextupdate'
    | 'announcementsBadge'
    | 'dragZoneWidth'
    | 'screenshotCapture'
    | 'imagePreview';

interface BasePostMessage {
    event: MessageEventType;
    data: unknown;
}

interface AttachmentStateMessage extends BasePostMessage {
    event: 'attachmentStateChanged';
    data: {
        hasAttachment: boolean;
    };
}

interface HashChangeMessage extends BasePostMessage {
    event: 'hashchange';
    data: {
        hash: string;
        title?: string;
    };
}

interface ContextUpdateMessage extends BasePostMessage {
    event: 'aiassitantcontextupdate';
    data: Record<string, unknown>;
}

interface AnnouncementsBadgeMessage extends BasePostMessage {
    event: 'announcementsBadge';
    data: {
        /** Show or hide the red dot on the host's floating action button. */
        visible: boolean;
    };
}

// Exported (unlike the other event data shapes above) so iframeWindow.ts — the host-page side
// receiving this message — can reuse the exact same type instead of redeclaring it, per #2078.
export interface DragZoneWidthData {
    /**
     * How far (in px) the empty, non-interactive part of the inline header extends from its
     * left edge. The host page's drag handle overlay sits outside the iframe, so it can't see
     * this layout itself (#2077) — it has to be told, and re-told whenever it changes (locale,
     * container width, whether the feedback pill is showing).
     */
    width: number;
}

interface DragZoneWidthMessage extends BasePostMessage {
    event: 'dragZoneWidth';
    data: DragZoneWidthData;
}

// Exported for iframeWindow.ts (host side), like DragZoneWidthData.
export interface ScreenshotCaptureData {
    /**
     * 'start' right before the LiveChat screen picker opens, 'end' once the frame is grabbed or the
     * picker is dismissed. The host hides the widget in between so the captured frame shows
     * the host application without the chat panel on top of it.
     */
    phase: 'start' | 'end';
}

interface ScreenshotCaptureMessage extends BasePostMessage {
    event: 'screenshotCapture';
    data: ScreenshotCaptureData;
}

export interface ImagePreviewData {
    open: boolean;
}

interface ImagePreviewMessage extends BasePostMessage {
    event: 'imagePreview';
    data: ImagePreviewData;
}

export type PostMessage =
    | AttachmentStateMessage
    | HashChangeMessage
    | ContextUpdateMessage
    | AnnouncementsBadgeMessage
    | DragZoneWidthMessage
    | ScreenshotCaptureMessage
    | ImagePreviewMessage;

export const postMessageToParent = (message: PostMessage): void => {
    if (window.parent !== window) {
        // CWE-668 (#2113): pin the validated embedder origin instead of broadcasting to '*'.
        window.parent.postMessage(message, getParentTargetOrigin());
    }
};
